import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';
import {
  BarChart3,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  LineChart,
  Search,
  SlidersHorizontal,
} from 'lucide-react';
import type { ModelViewResponse } from '@/lib/api';
import {
  buildTree,
  type TreeLeaf,
  type TreeSection,
  type TreeSectionId,
} from './tree-builder';
import { formatCellValueFragment } from './cell-style';
import { colToA1 } from './grid-helpers';

// ---------------------------------------------------------------------------
// Phase 2 S5 — TreeNavigatorRef (imperative handle for focus)
// ---------------------------------------------------------------------------

/**
 * Imperative handle exposed via forwardRef so ModelLayout can drive the
 * TreeNavigator search input and result-cycle from its top-level keyboard
 * handler (ADR-0008 two-mount design + supersession 2026-05-25 — cross-pane
 * shortcuts live on ModelLayout root).
 *
 * - focusSearchInput — Ctrl+F / Cmd+F path (Phase 2 Session 5).
 * - cycleToNextMatch / cycleToPrevMatch — Ctrl+G / Cmd+G + Ctrl+Shift+G /
 *   Cmd+Shift+G paths (Phase 3 Session 2). Cycle reads gridMatches (already
 *   iterable per research/phase-3.md § 4 Block G); falls back to focusing
 *   search input when gridMatches is empty (parity with Ctrl+F).
 */
export interface TreeNavigatorRef {
  focusSearchInput: () => void;
  cycleToNextMatch: () => void;
  cycleToPrevMatch: () => void;
}

export interface TreeNavigatorProps {
  schema: ModelViewResponse['schema'];
  visibleSheetNames: readonly string[];
  /**
   * Full layout payload. When supplied, the extended grid-cell matcher
   * (Phase 2 S5) walks `layout.sheets[].cells[]` to find matches against
   * `cell.text` (case-insensitive substring) and the formatted cell value
   * via `formatCellValueFragment`. Results appear in a "Found in grid"
   * sub-section below the existing tree categories.
   *
   * Optional — falls back to slug-only tree-level search when omitted
   * (back-compat for callers that only supply schema + visibleSheetNames).
   */
  layout?: ModelViewResponse['layout'];
  /**
   * Resolved cell values keyed by `${sheet}!${A1}${row}` (same key-space
   * as `ModelViewResponse.cellValues`). Required by the formatted-value
   * arm of the extended grid matcher so the value displayed in the
   * "Found in grid" result matches what the grid renders.
   *
   * Optional — the matcher falls back to showing `cell.text` as preview
   * when cellValues is absent or the cell has no entry.
   */
  cellValues?: Record<string, unknown>;
  /**
   * Currently-selected grid cell (drives the active-leaf highlight). Phase 4
   * Session 1 ships read-only highlighting; the parent's `setSelectedCell`
   * dispatches on tree click via `onLeafSelect`.
   */
  selectedCell: { row: number; col: number; sheet: string } | null;
  /**
   * Unique id of the tree-selected leaf. Drives the active-leaf highlight by
   * LEAF IDENTITY (not by cell), so when several schema entries map to the SAME
   * cell their leaves don't all light up at once. Set on tree-leaf click; null
   * for grid-originated / search-originated selection, in which case the Leaf
   * falls back to the legacy cell match. (tree-nav multi-select fix, 2026-06-11.)
   *
   * Optional — defaults to null so callers/tests that only drive `selectedCell`
   * keep the legacy cell-match highlight unchanged.
   */
  selectedLeafId?: string | null;
  onLeafSelect: (
    target: { sheet: string; row: number; col: number },
    leafId?: string
  ) => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
  /**
   * URL-driven initial search term + write-back. When provided, the
   * navigator hydrates the search box from `searchParam` on mount + after
   * any URL change (parent's `useSearch` is the source of truth);
   * write-back fires through `onSearchChange` debounced 250ms so the URL
   * stays in lockstep without thrashing the navigation stack.
   *
   * Both undefined → component owns its search state internally
   * (back-compat for callers that don't want URL-sync).
   * (Phase 3 Session 5)
   */
  searchParam?: string;
  onSearchChange?: (next: string | undefined) => void;
}

const SECTION_ICONS: Record<TreeSectionId, typeof BarChart3> = {
  outputs: BarChart3,
  inputs: SlidersHorizontal,
  cashflows: LineChart,
};

/** Cap on "Found in grid" results — prevents UI freeze on common substrings. */
const GRID_MATCH_LIMIT = 50;

/**
 * `<TreeNavigator>` — left-aside tree for the Model tab (in-platform-model-
 * view Phase 4 Session 1). Replaces `<TreeNavigatorPlaceholder>`.
 *
 * Layout:
 *   - Expanded: 240px wide. Search box (debounced 150ms) at top, scrollable
 *     section list below. Each section header always visible; branches
 *     collapse/expand on click; leaves are buttons that dispatch
 *     `onLeafSelect` on click.
 *   - Collapsed: 40px wide, 3 section icons stacked, expand chevron at top.
 *
 * Auto-tree from `buildTree(schema, visibleSheetNames)` — see `tree-builder.ts`
 * for grouping rules + the displayConfig fallback note.
 *
 * Phase 2 S5: converted to `forwardRef` to expose `focusSearchInput()` so
 * `<ModelLayout>` can focus the search input on Ctrl+F / Cmd+F (ADR-0008
 * two-mount design — Ctrl+F on ModelLayout root, NOT grid-root).
 */
export const TreeNavigator = forwardRef<TreeNavigatorRef, TreeNavigatorProps>(
  function TreeNavigator(
    {
      schema,
      visibleSheetNames,
      layout,
      cellValues,
      selectedCell,
      selectedLeafId = null,
      onLeafSelect,
      collapsed,
      onToggleCollapse,
      searchParam,
      onSearchChange,
    }: TreeNavigatorProps,
    ref
  ) {
    // --- Phase 2 S5: imperative ref for focus ---
    const searchInputRef = useRef<HTMLInputElement | null>(null);

    // --- Phase 3 S2: current cycle index into gridMatches. -1 = before first
    // match (next Ctrl+G goes to index 0). Resets to -1 on new search results
    // (new gridMatches identity) so cycling always starts from match #0 after
    // a new search term is entered.
    const [currentMatchIndex, setCurrentMatchIndex] = useState<number>(-1);

    const sections = useMemo(
      () => buildTree(schema, visibleSheetNames),
      [schema, visibleSheetNames]
    );

    // Search: input is the live string; term is the debounced applied value.
    // When the URL-sync wiring is active (`searchParam` + `onSearchChange`
    // both supplied), the URL is the source of truth — `searchInput`
    // hydrates from `searchParam` on mount AND when `searchParam` changes
    // (tab-switch persistence + bookmark restore). Write-back fires through
    // a 250ms-debounced `onSearchChange` (Phase 3 Session 5 contract).
    const [searchInput, setSearchInput] = useState(searchParam ?? '');
    const [searchTerm, setSearchTerm] = useState(searchParam ?? '');
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        setSearchTerm(searchInput);
      }, 150);
      return () => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
      };
    }, [searchInput]);

    // URL → state hydration. Watches `searchParam` ONLY (Q16 lock from
    // blueprint) so that programmatic URL changes (deep-link arrival,
    // back/forward navigation, route's `?search=` mutation) re-hydrate the
    // input. Write-back loop is bounded — `onSearchChange` only fires from
    // the user-driven input handler below, never from this effect.
    useEffect(() => {
      if (searchParam === undefined) return;
      setSearchInput(searchParam);
    }, [searchParam]);

    // State → URL write-back, debounced 250ms. Skipped when not wired,
    // and short-circuited when the in-flight value already matches the
    // URL state (avoids a redundant replace-state navigation right after
    // a programmatic URL change re-hydrates `searchInput`).
    const writeBackRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => {
      if (!onSearchChange) return undefined;
      const next = searchInput.length > 0 ? searchInput : undefined;
      if (next === searchParam) return undefined;
      if (writeBackRef.current) clearTimeout(writeBackRef.current);
      writeBackRef.current = setTimeout(() => {
        onSearchChange(next);
      }, 250);
      return () => {
        if (writeBackRef.current) clearTimeout(writeBackRef.current);
      };
    }, [searchInput, searchParam, onSearchChange]);

    // Per-branch collapsed state — defaults to "expanded" (branch open).
    const [collapsedBranchIds, setCollapsedBranchIds] = useState<Set<string>>(
      () => new Set()
    );
    const toggleBranch = (id: string) => {
      setCollapsedBranchIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    };

    // Phase 2 S5 — Extended grid-cell matcher.
    // Walks `layout.sheets[].cells[]` checking:
    //   (a) cell.text case-insensitive substring match (label text)
    //   (b) formatted cell value substring match (via formatCellValueFragment)
    // Only runs when the search term is non-empty. Capped at GRID_MATCH_LIMIT
    // entries to avoid UI freeze when a common substring like "0" appears
    // everywhere.
    const gridMatches = useMemo(() => {
      const term = searchTerm.trim().toLowerCase();
      if (!term || !layout) return [];

      const matches: Array<{
        sheet: string;
        row: number;
        col: number;
        previewText: string;
      }> = [];

      outer: for (const sheetData of layout.sheets) {
        for (const cell of sheetData.cells ?? []) {
          // (a) label-text match — case-insensitive substring
          const textMatches = Boolean(
            cell.text && cell.text.toLowerCase().includes(term)
          );

          // (b) formatted-value match — compose Phase 1's formatCellValueFragment
          // We look up the cell value from cellValues using the canonical key.
          const cellKey = `${sheetData.name}!${colToA1(cell.col)}${cell.row}`;
          const rawValue = cellValues?.[cellKey];
          const formatted =
            formatCellValueFragment(rawValue, cell, schema).body ?? '';
          const valueMatches = formatted.toLowerCase().includes(term);

          if (textMatches || valueMatches) {
            matches.push({
              sheet: sheetData.name,
              row: cell.row,
              col: cell.col,
              // Prefer label text as preview; fall back to formatted value
              previewText: cell.text ?? formatted,
            });
            if (matches.length >= GRID_MATCH_LIMIT) break outer;
          }
        }
      }

      return matches;
    }, [searchTerm, layout, cellValues, schema]);

    // Phase 3 S2 — reset cycle index when search results change. New
    // gridMatches array identity = new search term → start from match #0
    // on next Ctrl+G (currentMatchIndex -1 → first next = 0).
    useEffect(() => {
      setCurrentMatchIndex(-1);
    }, [gridMatches]);

    // Phase 3 S2 — extended useImperativeHandle (replaces the initial Phase 2
    // S5 version above which only exposed focusSearchInput). Placed here, after
    // gridMatches, so the cycle methods close over the latest gridMatches ref.
    useImperativeHandle(
      ref,
      () => ({
        focusSearchInput: () => searchInputRef.current?.focus(),
        cycleToNextMatch: () => {
          if (gridMatches.length === 0) {
            // Empty matcher — parity with Ctrl+F: focus the search input so
            // the user can type a query and then hit Ctrl+G again.
            searchInputRef.current?.focus();
            return;
          }
          const next = (currentMatchIndex + 1) % gridMatches.length;
          setCurrentMatchIndex(next);
          const m = gridMatches[next];
          onLeafSelect({ sheet: m.sheet, row: m.row, col: m.col });
        },
        cycleToPrevMatch: () => {
          if (gridMatches.length === 0) {
            searchInputRef.current?.focus();
            return;
          }
          // Initial -1 sentinel ("no match selected yet") → first Prev goes to the LAST
          // match (intuitive wrap behavior, mirrors browser Find Previous semantics).
          // Otherwise: modular subtraction with positive wrap.
          const prev =
            currentMatchIndex < 0
              ? gridMatches.length - 1
              : (currentMatchIndex - 1 + gridMatches.length) %
                gridMatches.length;
          setCurrentMatchIndex(prev);
          const m = gridMatches[prev];
          onLeafSelect({ sheet: m.sheet, row: m.row, col: m.col });
        },
      }),

      [gridMatches, currentMatchIndex, onLeafSelect]
    );

    if (collapsed) {
      return (
        <aside
          className="w-10 shrink-0 border-r border-panel-border bg-panel-bg flex flex-col items-center pt-2 gap-3"
          aria-label="Model navigator"
          aria-expanded={false}
          data-testid="tree-navigator"
        >
          <button
            type="button"
            onClick={onToggleCollapse}
            className="text-text-muted hover:text-text-bright cursor-pointer bg-transparent border-none p-1"
            aria-label="Expand navigator"
            data-testid="tree-toggle-collapse"
          >
            <ChevronRight size={14} />
          </button>
          {sections.map((s) => {
            const Icon = SECTION_ICONS[s.id];
            return (
              <span
                key={s.id}
                className="w-6 h-6 flex items-center justify-center text-text-muted"
                aria-label={s.label}
                title={s.label}
                data-testid={`tree-section-icon-${s.id}`}
              >
                <Icon size={14} />
              </span>
            );
          })}
        </aside>
      );
    }

    const term = searchTerm.trim().toLowerCase();
    const matches = (label: string) =>
      term === '' || label.toLowerCase().includes(term);

    return (
      <aside
        className="w-60 shrink-0 border-r border-panel-border bg-panel-bg flex flex-col"
        aria-label="Model navigator"
        aria-expanded={true}
        data-testid="tree-navigator"
      >
        <div className="flex items-center justify-between px-3 py-2 border-b border-panel-border">
          <span className="text-xs font-mono font-semibold text-text-muted tracking-[1.4px]">
            NAVIGATOR
          </span>
          <button
            type="button"
            onClick={onToggleCollapse}
            className="text-text-muted hover:text-text-bright cursor-pointer bg-transparent border-none p-1"
            aria-label="Collapse navigator"
            data-testid="tree-toggle-collapse"
          >
            <ChevronLeft size={14} />
          </button>
        </div>
        <div className="px-2 py-2 border-b border-panel-border">
          <div className="relative">
            <Search
              size={12}
              className="absolute left-2 top-1/2 -translate-y-1/2 text-text-dim pointer-events-none"
            />
            <input
              ref={searchInputRef}
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search..."
              aria-label="Search navigator"
              data-testid="tree-search-input"
              className="w-full pl-7 pr-2 py-1.5 bg-input-bg border border-input-border rounded-[3px] text-text-bright text-xs font-mono outline-none focus:border-accent/30"
            />
          </div>
        </div>
        <div
          className="flex-1 overflow-y-auto"
          data-testid="tree-navigator-body"
        >
          {sections.map((section) => (
            <SectionRenderer
              key={section.id}
              section={section}
              matches={matches}
              collapsedBranchIds={collapsedBranchIds}
              onToggleBranch={toggleBranch}
              onLeafSelect={onLeafSelect}
              selectedCell={selectedCell}
              selectedLeafId={selectedLeafId}
            />
          ))}

          {/* Phase 2 S5 — "Found in grid" sub-section (extended cell matcher) */}
          {gridMatches.length > 0 && (
            <section
              className="border-b border-panel-border"
              data-testid="tree-found-in-grid"
            >
              <div className="px-3 py-1.5 text-xs font-sans font-bold text-text-bright tracking-[0.5px] uppercase">
                Found in grid
              </div>
              <ul className="list-none m-0 p-0">
                {gridMatches.map((match) => (
                  <li key={`${match.sheet}-${match.row}-${match.col}`}>
                    <button
                      type="button"
                      onClick={() =>
                        onLeafSelect({
                          sheet: match.sheet,
                          row: match.row,
                          col: match.col,
                        })
                      }
                      className="w-full px-3 py-1 text-left text-xs font-mono text-text hover:text-text-bright hover:bg-panel-border-hi cursor-pointer bg-transparent border-none"
                      data-testid={`tree-grid-match-${match.sheet}-${match.row}-${match.col}`}
                    >
                      <span className="text-text-muted">
                        {match.sheet}!{colToA1(match.col)}
                        {match.row}
                      </span>{' '}
                      <span className="truncate">{match.previewText}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </aside>
    );
  }
);

interface SectionRendererProps {
  section: TreeSection;
  matches: (label: string) => boolean;
  collapsedBranchIds: Set<string>;
  onToggleBranch: (id: string) => void;
  onLeafSelect: (
    target: { sheet: string; row: number; col: number },
    leafId?: string
  ) => void;
  selectedCell: TreeNavigatorProps['selectedCell'];
  selectedLeafId: string | null;
}

function SectionRenderer({
  section,
  matches,
  collapsedBranchIds,
  onToggleBranch,
  onLeafSelect,
  selectedCell,
  selectedLeafId,
}: SectionRendererProps) {
  const visibleBranches = section.branches
    .map((b) => ({
      ...b,
      children: b.children.filter((c) => matches(c.label)),
    }))
    .filter((b) => b.children.length > 0);
  const visibleLeaves = section.leaves.filter((l) => matches(l.label));

  // Section header always renders (even with zero matching nodes) so the
  // structure stays orientable while searching. Non-matching branches/leaves
  // simply don't render under the header.
  return (
    <div
      className="border-b border-panel-border"
      data-testid={`tree-section-${section.id}`}
    >
      <div className="px-3 py-1.5 text-xs font-sans font-bold text-text-bright tracking-[0.5px] uppercase">
        {section.label}
      </div>
      {visibleBranches.map((branch) => {
        const isOpen = !collapsedBranchIds.has(branch.id);
        return (
          <div key={branch.id} data-testid={`tree-branch-${branch.id}`}>
            <button
              type="button"
              onClick={() => onToggleBranch(branch.id)}
              aria-expanded={isOpen}
              className="w-full flex items-center gap-1 px-3 py-1 text-xs font-mono text-text-muted hover:text-text-bright cursor-pointer bg-transparent border-none"
            >
              {isOpen ? (
                <ChevronDown size={10} className="shrink-0" />
              ) : (
                <ChevronRight size={10} className="shrink-0" />
              )}
              <span className="truncate">{branch.label}</span>
              <span className="ml-auto text-text-dim text-[10px] tabular-nums">
                {branch.children.length}
              </span>
            </button>
            {isOpen &&
              branch.children.map((leaf) => (
                <Leaf
                  key={leaf.id}
                  leaf={leaf}
                  onSelect={onLeafSelect}
                  selectedCell={selectedCell}
                  selectedLeafId={selectedLeafId}
                  indent="pl-7"
                />
              ))}
          </div>
        );
      })}
      {visibleLeaves.map((leaf) => (
        <Leaf
          key={leaf.id}
          leaf={leaf}
          onSelect={onLeafSelect}
          selectedCell={selectedCell}
          selectedLeafId={selectedLeafId}
          indent="pl-3"
        />
      ))}
    </div>
  );
}

function Leaf({
  leaf,
  onSelect,
  selectedCell,
  selectedLeafId,
  indent,
}: {
  leaf: TreeLeaf;
  onSelect: (
    target: { sheet: string; row: number; col: number },
    leafId?: string
  ) => void;
  selectedCell: TreeNavigatorProps['selectedCell'];
  selectedLeafId: string | null;
  indent: string;
}) {
  const target = leaf.target;
  const disabled = target === null;
  // Identity-first: when a tree leaf is the selection source, highlight by the
  // unique leaf id so duplicate-cell leaves (multiple schema entries mapping to
  // the same cell) don't all light up. Fall back to the legacy cell match only
  // for grid-/search-originated selection (selectedLeafId === null).
  // (tree-nav multi-select fix, 2026-06-11.)
  const isActive =
    target !== null &&
    (selectedLeafId !== null
      ? leaf.id === selectedLeafId
      : selectedCell !== null &&
        selectedCell.sheet === target.sheet &&
        selectedCell.row === target.row &&
        selectedCell.col === target.col);
  return (
    <button
      type="button"
      onClick={() => {
        if (target) onSelect(target, leaf.id);
      }}
      disabled={disabled}
      data-testid={`tree-leaf-${leaf.id}`}
      data-active={isActive ? 'true' : undefined}
      className={clsx(
        'w-full text-left pr-3 py-1 text-xs font-mono truncate bg-transparent border-none',
        indent,
        disabled && 'text-text-dim cursor-default',
        !disabled &&
          !isActive &&
          'text-text hover:text-text-bright cursor-pointer',
        isActive && 'text-accent bg-accent-dim/20 cursor-pointer'
      )}
    >
      {leaf.label}
    </button>
  );
}
